import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import {
  getSessionLastActivity,
  isSessionRetired,
  type Session,
} from '@ais-ui/auth';
import { periodicCheck } from './ol-server/checkSearchAndRescue';

/** How often the olserver keep-alive runs. It also stops the backend from
 * idling the session out, so the user can walk away from the map. */
export const KEEP_ALIVE_INTERVAL_MS = 15_000;

/** How long a keep-alive may go unanswered before it counts as a miss. */
export const KEEP_ALIVE_TIMEOUT_MS = 10_000;

/** Consecutive misses before the transport is declared unresponsive. Two, so
 * one slow answer from a busy backend is forgiven. */
export const KEEP_ALIVE_MAX_MISSES = 2;

const TIMED_OUT = Symbol('keep-alive timed out');

/** How long the socket has been silent, or null if it never delivered a frame. */
export function getMsSinceLastFrame(session: Session): number | null {
  const lastFrameAt = getSessionLastActivity(session);
  return lastFrameAt === undefined
    ? null
    : Math.round(performance.now() - lastFrameAt);
}

// Only the unusual pings leave a trail; healthy ones would flood Sentry's
// small breadcrumb buffer.
function addKeepAliveBreadcrumb(
  message: string,
  data: Record<string, number | null>,
) {
  Sentry.addBreadcrumb({
    category: 'keep-alive',
    level: 'warning',
    message,
    data,
  });
}

/**
 * Keeps the olserver session alive, and notices when its socket has silently
 * died.
 *
 * A half-open socket - laptop sleep, a VPN or NAT dropping the connection -
 * never fires `onclose`, so without this the map just stops updating with no
 * reconnect and no feedback. A check is a miss when it goes unanswered, or
 * fails on a socket that is no longer open - unless its owner retired it
 * (`isSessionRetired`), which ends checking without a report. Any answer, even
 * an error - or any other frame the socket delivers meanwhile - proves the
 * transport is alive (session problems are `sessionError`'s job, a closed
 * socket `connectionClosed`'s - this only backs that up if it was missed).
 *
 * After {@link KEEP_ALIVE_MAX_MISSES} consecutive misses `onUnresponsive` is
 * called once with the session, and checking stops; a fresh olSession (a
 * reconnect) starts over with a clean slate.
 */
export function useOlSessionKeepAlive(
  olSession: Session | undefined,
  onUnresponsive: (session: Session) => void,
): void {
  // Latest-ref, so a new callback identity doesn't restart the interval and
  // reset the miss count.
  const onUnresponsiveRef = useRef(onUnresponsive);
  useEffect(() => {
    onUnresponsiveRef.current = onUnresponsive;
  }, [onUnresponsive]);

  useEffect(() => {
    if (!olSession) return;

    let misses = 0;
    // When the first ping of the current run of misses went out. A frame since
    // then still proves the socket is alive, even one that arrived after that
    // ping's deadline (a late reply) and before the next ping was sent.
    let firstMissSentAt: number | undefined;
    let active = true;
    const timeoutIds = new Set<ReturnType<typeof setTimeout>>();

    // `active` is what keeps a check still in flight from counting a miss
    // once this effect is done - it can still time out, or fail late.
    const stop = () => {
      active = false;
      clearInterval(intervalId);
      timeoutIds.forEach(clearTimeout);
      timeoutIds.clear();
    };

    const withTimeout = <T>(promise: Promise<T>) =>
      new Promise<T | typeof TIMED_OUT>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          timeoutIds.delete(timeoutId);
          resolve(TIMED_OUT);
        }, KEEP_ALIVE_TIMEOUT_MS);
        timeoutIds.add(timeoutId);
        promise.then(resolve, reject).finally(() => {
          clearTimeout(timeoutId);
          timeoutIds.delete(timeoutId);
        });
      });

    // A busy olserver can be slow to answer the ping while the socket is
    // alive and still delivering frames, so a frame since the ping went out
    // counts as proof of life too - as long as the socket is still open, or a
    // frame from before it closed would hide a close event that was missed.
    const hasHeardFromSocketSince = (sentAt: number) =>
      olSession.webSocket?.readyState === WebSocket.OPEN &&
      (getSessionLastActivity(olSession) ?? -Infinity) >= sentAt;

    // Any answer from the server - even an error - proves the transport is
    // alive. A rejection doesn't when the socket is no longer open: `request`
    // then fails locally, without anything having reached the server.
    const isAnswered = async (sentAt: number): Promise<boolean> => {
      try {
        const result = await withTimeout(periodicCheck(olSession));
        if (result !== TIMED_OUT) {
          return true;
        }
        const forgiven = hasHeardFromSocketSince(firstMissSentAt ?? sentAt);
        if (forgiven) {
          addKeepAliveBreadcrumb(
            'Ping unanswered, but the socket is still delivering',
            { msSinceLastFrame: getMsSinceLastFrame(olSession) },
          );
        }
        return forgiven;
      } catch (error) {
        console.error('Keep-alive check failed:', error);
        return olSession.webSocket?.readyState === WebSocket.OPEN;
      }
    };

    const check = async () => {
      const sentAt = performance.now();
      const answered = await isAnswered(sentAt);
      if (!active) {
        return;
      }
      // Retiring settles in-flight requests and closes the socket, which
      // would read as a miss. But the socket didn't go silent - its owner
      // (a logout, a reconnect replacing it) ended it and handles what's next.
      if (isSessionRetired(olSession)) {
        stop();
        return;
      }
      if (answered) {
        misses = 0;
        firstMissSentAt = undefined;
        return;
      }

      misses += 1;
      firstMissSentAt ??= sentAt;
      console.warn(
        `Keep-alive unanswered (${misses}/${KEEP_ALIVE_MAX_MISSES})`,
      );
      addKeepAliveBreadcrumb('Ping missed, socket silent', {
        misses,
        msSinceLastFrame: getMsSinceLastFrame(olSession),
      });
      if (misses >= KEEP_ALIVE_MAX_MISSES) {
        stop();
        onUnresponsiveRef.current(olSession);
      }
    };

    const intervalId = setInterval(check, KEEP_ALIVE_INTERVAL_MS);
    check();

    return stop;
  }, [olSession]);
}
